<template>
    <div>
      <breadcrumb :items="breadcrumbs" />
  
      <v-container fluid>
        <div class="d-flex align-center justify-space-between mt-6 mb-4">
          <h2 class="font-weight-bold mb-6">{{ userName }}</h2>
          <div>
            <v-btn
                outlined
                class="mr-2 mb-2"
                @click="$router.push({ name: 'listUsers' })"
                style="border-color: #00BD73; color: #00BD73; background-color: white;"
            >
                {{ $t('carbonCredit.back') }}
            </v-btn>
          </div>
        </div>
  
        <v-card flat class="pa-6">
            <!-- Project Details Expandable Section -->
            <v-expansion-panels flat class="mb-4" v-model="projectDetailsExpanded" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                        {{ $t('carbonCredit.user.projectDetails') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row class="mt-4" align="center">
                            <v-col cols="12" md="3">
                                <div class="view-block">
                                <div class="d-flex align-center">
                                    <label class="view-label">{{ $t('carbonCredit.register.projectTitle') }}</label>
                                    <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                        class="ml-2"
                                        size="20"
                                        style="color: #757575; cursor: pointer;"
                                        v-bind="attrs"
                                        v-on="on"
                                        >
                                        mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t('carbonCredit.register.projectTitle') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="view-value">{{ form.project_title }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                <div class="d-flex align-center">
                                    <label class="view-label">{{ $t('carbonCredit.user.country') }}</label>
                                    <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                        class="ml-2"
                                        size="20"
                                        style="color: #757575; cursor: pointer;"
                                        v-bind="attrs"
                                        v-on="on"
                                        >
                                        mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t('carbonCredit.user.country') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="view-value">{{ form.country }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                <div class="d-flex align-center">
                                    <label class="view-label">{{ $t('carbonCredit.user.dateJoined') }}</label>
                                    <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                        class="ml-2"
                                        size="20"
                                        style="color: #757575; cursor: pointer;"
                                        v-bind="attrs"
                                        v-on="on"
                                        >
                                        mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t('carbonCredit.user.dateJoined') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="view-value">{{ form.dateJoined }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                <div class="d-flex align-center">
                                    <label class="view-label">{{ $t('carbonCredit.user.cropGrowingPractice') }}</label>
                                    <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                        class="ml-2"
                                        size="20"
                                        style="color: #757575; cursor: pointer;"
                                        v-bind="attrs"
                                        v-on="on"
                                        >
                                        mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t('carbonCredit.user.cropGrowingPractice') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="view-value">{{ form.cropGrowingPractice }}</div>
                                </div>
                            </v-col>
                        </v-row>
                        
                        <!-- Action Buttons -->
                        <div class="d-flex justify-end mt-6 mb-4">
                            <v-btn
                                :outlined="!allFarmsRejected"
                                :color="allFarmsRejected ? '#FF5252' : '#FF5252'"
                                :class="allFarmsRejected ? 'white--text font-weight-bold mr-3' : 'mr-3 font-weight-bold'"
                                @click="rejectAll"
                                style="text-transform: uppercase;"
                            >
                                <v-icon v-if="allFarmsRejected" left class="white--text">mdi-check</v-icon>
                                {{ allFarmsRejected ? 'REJECTED' : $t('carbonCredit.user.rejectAll') }}
                            </v-btn>
                            <v-btn
                                :outlined="!allFarmsAccepted"
                                :color="allFarmsAccepted ? '#00BD73' : '#00BD73'"
                                :class="allFarmsAccepted ? 'white--text font-weight-bold' : 'font-weight-bold'"
                                @click="acceptAll"
                                style="text-transform: uppercase;"
                            >
                                <v-icon v-if="allFarmsAccepted" left class="white--text">mdi-check</v-icon>
                                {{ allFarmsAccepted ? 'ACCEPTED' : $t('carbonCredit.user.acceptAll') }}
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <!-- Farms Expandable Section -->
            <v-expansion-panels flat class="mb-4" v-model="expandedFarmPanels" multiple>
                <v-expansion-panel v-for="(farm, index) in projectFarms" :key="farm.id || index" class="mb-4">
                    <v-expansion-panel-header class="font-weight-medium bordered-expansion">
                        {{ $t('carbonCredit.user.farm') }}: {{ farm.name }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <div v-if="loadingFarms.has(farm.id) && !verifiedFarms.has(farm.id)" class="text-center py-8">
                            <v-progress-circular
                                indeterminate
                                color="#00BD73"
                                size="50"
                                width="4"
                            ></v-progress-circular>
                        </div>
                        <template v-else>
                        <v-row class="mt-4" align="center">
                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.dateOfRegistration') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.dateOfRegistration') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.dateOfRegistration }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmName') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmName') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.name }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmRegistrationId') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmRegistrationId') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.farmRegistrationId }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.dimitraFarmId') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.dimitraFarmId') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.dimitraFarmId }}</div>
                                </div>
                            </v-col>
                        </v-row>

                        <v-row class="mt-4" align="center">
                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.notTheFarmer') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.notTheFarmer') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.notTheFarmer }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmerFirstName') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmerFirstName') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.farmerFirstName }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmerMiddleName') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmerMiddleName') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.farmerMiddleName }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmerLastName') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmerLastName') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.farmerLastName }}</div>
                                </div>
                            </v-col>
                        </v-row>

                        <v-row class="mt-4" align="center">
                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmerRegistrationId') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmerRegistrationId') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.farmerRegistrationId }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.dimitraFarmerId') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.dimitraFarmerId') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.dimitraFarmerId }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmGeofence') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmGeofence') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value d-flex align-center">
                                        <span class="mr-2">{{ farm.farmGeofence }}</span>
                                        <v-btn
                                            text
                                            x-small
                                            :color="farm.geofenceAvailable ? '#00BD73' : '#CCCCCC'"
                                            :disabled="!farm.geofenceAvailable"
                                            @click="viewGeofence(farm.id)"
                                            style="min-width: auto; padding: 0 8px;"
                                        >
                                            {{ $t('carbonCredit.user.viewGeofence') }}
                                        </v-btn>
                                    </div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.totalFarmSize') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.totalFarmSize') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.totalFarmSize }}</div>
                                </div>
                            </v-col>
                        </v-row>

                        <v-row class="mt-4" align="center">
                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.mainGoals') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.mainGoals') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.mainGoals }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.ownershipType') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.ownershipType') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.ownershipType }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.certifications') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.certifications') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.certifications }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.productionType') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.productionType') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.productionType }}</div>
                                </div>
                            </v-col>
                        </v-row>

                        <v-row class="mt-4" align="center">
                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.landTitle') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.landTitle') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">
                                        {{ farm.landTitle }}
                                        <v-btn
                                            text
                                            small
                                            color="#00BD73"
                                            class="ml-2"
                                            @click="viewLandTitle(farm.id)"
                                        >
                                            {{ $t('carbonCredit.user.view') }}
                                        </v-btn>
                                    </div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmLocationAddress') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmLocationAddress') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.farmLocationAddress }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.farmCoordinate') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.farmCoordinate') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value">{{ farm.farmCoordinate }}</div>
                                </div>
                            </v-col>

                            <v-col cols="12" md="3">
                                <div class="view-block">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ $t('carbonCredit.user.status') }}</label>
                                        <v-tooltip top color="grey" max-width="300" content-class="filter-summary-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon
                                                    class="ml-2"
                                                    size="20"
                                                    style="color: #757575; cursor: pointer;"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('carbonCredit.user.status') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="view-value d-flex align-center">
                                        <v-icon
                                            v-if="getFarmDisplayStatus(farm).warning"
                                            class="mr-2"
                                            color="#F59E0B"
                                            size="20"
                                        >
                                            mdi-alert
                                        </v-icon>
                                        {{ getFarmDisplayStatus(farm).label }}
                                    </div>
                                </div>
                            </v-col>
                        </v-row>

                        <!-- Farmer Agreement and Acceptance Status Sections -->
                        <v-row class="mt-6">
                            <v-col cols="12" md="4" v-if="farm.projectFarmId">
                                <div class="d-flex align-center mb-2">
                                    <label class="view-value bold-title">{{ $t('carbonCredit.user.farmActivitySubmissions') }}</label>
                                    <v-tooltip top color="grey" max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon
                                                class="ml-2"
                                                size="20"
                                                style="color: #00BD73; cursor: pointer;"
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                mdi-alert-circle
                                            </v-icon>
                                        </template>
                                        <span>{{ $t('carbonCredit.user.farmActivitySubmissions') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="view-value mb-3 normal-text description-height">{{ $t('carbonCredit.user.farmActivitySubmissionsDesc') }}</div>
                                <div class="button-container">
                                    <v-btn
                                        color="#00BD73"
                                        class="white--text font-weight-bold"
                                        @click="viewFarmData(farm.id)"
                                        style="text-transform: uppercase;"
                                    >
                                        {{ $t('carbonCredit.user.viewFarmData') }}
                                    </v-btn>
                                </div>
                            </v-col>

                            <v-col v-if="farm.projectFarmId" cols="12" md="4">
                                <div class="d-flex align-center mb-2">
                                    <label class="view-value bold-title">{{ $t('carbonCredit.user.farmerAgreement') }}</label>
                                    <v-tooltip top color="grey" max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon
                                                class="ml-2"
                                                size="20"
                                                style="color: #00BD73; cursor: pointer;"
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                mdi-alert-circle
                                            </v-icon>
                                        </template>
                                        <span>{{ $t('carbonCredit.user.farmerAgreement') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="view-value mb-3 normal-text description-height">
                                    <span v-if="farm.signedAgreementUrl">
                                        {{ $t('carbonCredit.user.viewSignedAgreement') }}
                                    </span>
                                    <span v-else>
                                        {{ $t('carbonCredit.user.noSignedAgreement') }}
                                    </span>
                                </div>
                                <div class="button-container mb-2">
                                    <v-btn
                                        :color="farm.signedAgreementUrl ? '#00BD73' : '#CCCCCC'"
                                        :disabled="!farm.signedAgreementUrl"
                                        class="white--text font-weight-bold"
                                        @click="viewAgreement(farm.id)"
                                        style="text-transform: uppercase;"
                                    >
                                        <v-icon left class="white--text">mdi-file-document-outline</v-icon>
                                        {{ $t('carbonCredit.user.viewAgreement') }}
                                    </v-btn>
                                </div>
                                <div class="button-container">
                                    <v-btn
                                        outlined
                                        color="#00BD73"
                                        class="font-weight-bold"
                                        @click="openAgreementUploadDialog(farm.id)"
                                        style="text-transform: uppercase;"
                                    >
                                        <v-icon left color="#00BD73">mdi-upload</v-icon>
                                        {{
                                          farm.signedAgreementUrl
                                            ? $t('carbonCredit.user.replaceAgreement')
                                            : $t('carbonCredit.user.uploadAgreement')
                                        }}
                                    </v-btn>
                                </div>
                            </v-col>

                            <v-col cols="12" :md="farm.projectFarmId ? 4 : 6">
                                <div class="d-flex align-center mb-2">
                                    <label class="view-value bold-title">{{ $t('carbonCredit.user.acceptanceStatus') }}</label>
                                    <v-tooltip top color="grey" max-width="300">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon
                                                class="ml-2"
                                                size="20"
                                                style="color: #00BD73; cursor: pointer;"
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                mdi-alert-circle
                                            </v-icon>
                                        </template>
                                        <span>{{ $t('carbonCredit.user.acceptanceStatus') }}</span>
                                    </v-tooltip>
                                </div>
                                <div class="view-value mb-3 normal-text description-height">{{ $t('carbonCredit.user.currentStatus') }}: {{ getFarmDisplayStatus(farm).label }}</div>
                                <div class="button-container">
                                    <div class="d-flex">
                                        <v-btn
                                            :outlined="farm.status === 'Accepted' || farm.status === 'Pending'"
                                            :color="farm.status === 'Rejected' ? '#FF5252' : '#FF5252'"
                                            :class="[
                                                farm.status === 'Rejected' ? 'white--text font-weight-bold' : 'font-weight-bold',
                                                'mr-3'
                                            ]"
                                            @click="rejectFarm(farm.id)"
                                            style="text-transform: uppercase;"
                                        >
                                            <v-icon 
                                                v-if="farm.status === 'Rejected'" 
                                                left 
                                                class="white--text"
                                            >
                                                mdi-check
                                            </v-icon>
                                            {{ farm.status === 'Rejected' ? 'REJECTED' : 'REJECT' }}
                                        </v-btn>
                                        <v-btn
                                            :outlined="farm.status === 'Rejected' || farm.status === 'Pending'"
                                            :color="farm.status === 'Accepted' ? '#00BD73' : '#00BD73'"
                                            :class="[
                                                farm.status === 'Accepted' ? 'white--text font-weight-bold' : 'font-weight-bold'
                                            ]"
                                            @click="acceptFarm(farm.id)"
                                            style="text-transform: uppercase;"
                                        >
                                            <v-icon 
                                                v-if="farm.status === 'Accepted'" 
                                                left 
                                                class="white--text"
                                            >
                                                mdi-check
                                            </v-icon>
                                            {{ farm.status === 'Accepted' ? 'ACCEPTED' : 'ACCEPT' }}
                                        </v-btn>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                        </template>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <!-- Remove the separate sections -->
        </v-card>
      </v-container>
      
      <!-- Confirmation Dialog -->
      <ConfirmationBox
        :dialog="showConfirmDialog"
        :actionType="confirmActionType === 'rejectAll' || confirmActionType === 'rejectFarm' ? 'reject' : 'accept'"
        :message="confirmMessage"
        :dialogTitle="confirmTitle"
        :confirmButtonText="confirmButtonText"
        @close="showConfirmDialog = false"
        @confirm="handleConfirmAction"
      />
      
      <!-- View Geofence Dialog -->
      <ViewGeofence
        :dialog="showGeofenceDialog"
        :farmDetails="selectedFarmForGeofence"
        :farmId="selectedFarmForGeofence?.farm?.id"
        :disableEditGeofence="true"
        :diligenceReportId="0"
        @close="closeGeofenceDialog"
      />

      <!-- Upload / Replace Signed Agreement Dialog -->
      <v-dialog
        v-model="showAgreementUploadDialog"
        transition="dialog-top-transition"
        max-width="600"
        persistent
      >
        <v-card>
          <v-toolbar color="primary" dark flat dense>
            <v-toolbar-title class="font-weight-bold">
              {{
                agreementUploadFarm && agreementUploadFarm.signedAgreementUrl
                  ? $t('carbonCredit.user.replaceAgreementTitle')
                  : $t('carbonCredit.user.uploadAgreementTitle')
              }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon class="white--text" @click="closeAgreementUploadDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-form @submit.prevent="submitAgreementUpload">
            <v-card-text>
              <p class="mb-4">
                {{ $t('carbonCredit.user.uploadAgreementDescription') }}
              </p>

              <v-row>
                <v-col cols="12">
                  <label class="font-weight-medium mb-1">
                    {{ $t('carbonCredit.register.selectFile') }}
                  </label>
                  <div class="upload-document mt-2">
                    <div
                      :class="['dropZone', { 'dropZone-over': agreementDragging }]"
                      @dragenter="agreementDragging = true"
                      @dragleave="agreementDragging = false"
                      @drop="handleAgreementDrop($event)"
                    >
                      <div
                        class="dropZone-info d-flex justify-center align-center"
                        @dragover.prevent
                        @click="triggerAgreementFileInput"
                      >
                        <v-icon size="50" color="#0057BD">mdi-tray-arrow-up</v-icon>
                        <div class="dropZone-upload-limit-info">
                          <h2 class="ml-4 blue-color underline">
                            {{ $t('carbonCredit.register.selectFile') }}
                          </h2>
                          <p class="gray-color">
                            {{ $t('carbonCredit.register.dragDropFiles') }}
                          </p>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="application/pdf,.pdf,.jpg,.jpeg,.png,image/jpeg,image/png"
                        @change="onAgreementFileChange($event)"
                        ref="agreementFileInput"
                      />
                    </div>
                  </div>

                  <template v-if="agreementDocuments.length">
                    <div
                      class="pdf-docs mt-6 d-flex flex-column flex-sm-row"
                      v-for="(doc, index) in agreementDocuments"
                      :key="`agreement-doc-${index}-${doc.name || doc.file_name || doc.id || index}`"
                    >
                      <div class="docs-box mr-4 mb-4 mb-sm-0">
                        <div class="docs-preview">
                          <div class="img-item">
                            <div class="pdf-box">
                              <v-icon class="delete-icon" @click="removeAgreementFile">
                                mdi-trash-can-outline
                              </v-icon>
                              <h1 class="file-extension">
                                {{ getAgreementFileExtension(doc) }}
                              </h1>
                            </div>
                            <span class="file-name">
                              {{ getAgreementFileName(doc) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                outlined
                @click="closeAgreementUploadDialog"
                class="mr-2"
                depressed
                style="min-width: 150px;"
                color="primary"
              >
                {{ $t('carbonCredit.cancel') }}
              </v-btn>
              <v-btn
                type="submit"
                color="primary"
                style="min-width: 150px;"
                :loading="uploadingAgreement"
                :disabled="!agreementFile"
              >
                {{ $t('carbonCredit.submit') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import loadingMixin from '@/mixins/LoadingMixin';
  import CarbonCreditService from '../../../_services/CarbonCreditService';
  import ConfirmationBox from './components/ConfirmationBox.vue';
  import ViewGeofence from './components/ViewGeofence.vue';
  import FarmService from '@/_services/FarmService';

  export default {
    mixins: [loadingMixin],
    components: {
      ConfirmationBox,
      ViewGeofence
    },
    data() {
      return {
        projectId: null,
        showConfirmDialog: false,
        confirmActionType: '',
        confirmMessage: '',
        confirmTitle: '',
        confirmButtonText: '',
        pendingFarmId: null, // Store farm ID for single farm actions
        isInitialLoad: true, // Track if this is the initial load
        showGeofenceDialog: false,
        selectedFarmForGeofence: null,
        showAgreementUploadDialog: false,
        agreementUploadFarm: null,
        agreementFile: null,
        uploadingAgreement: false,
        agreementDragging: false,
        agreementDocuments: [],
        form: {
          project_title: '',
          project_type: '',
          description: '',
          status: '',
          creditType: '',
          creditStart: '',
          creditEnd: '',
          standard: '',
          country: '',
          dateJoined: '',
          cropGrowingPractice: '',
          validationDocumentation: [],
          agreement: [],
          headerImage: '',
        },
        orgSearch: '',
        organizations: [],
        filteredOrganizations: [],
        orgTableHeaders: [
          { text: 'Organizations Name', value: 'name' },
          { text: 'Organizations Address', value: 'address' },
          { text: 'Country', value: 'country' },
          { text: 'Website', value: 'website' },
        ],
        orgPage: 1,
        limit: 5,
        orgFrom: 1,
        orgTo: 5,
        orgTotalPages: 1,
        sdgs: [],
        selectedSdgs: [],
        currencies: ['USD', 'EUR', 'AUD'],
        vintageHeaders: [
          { text: 'Vintage Year', value: 'year' },
          { text: 'Estimated Credits', value: 'credits' },
          { text: 'Price Per Credit', value: 'price' }
        ],
        dataCycleHeaders: [
          { text: 'Module', value: 'name' },
          { text: 'Recurring', value: 'recurring' },
          { text: 'Score', value: 'score' },
          { text: 'Approval', value: 'approval' }
        ],
        dataCollectionModules: [],
        selectedMedia: null,
        allFilesForModal: [],
        office: null,
        userFarms: [],
        verifiedFarms: new Set(), // Track which farms have been verified for geofence
        loadingFarms: new Set(), // Track which farms are currently being loaded
        expandedFarmPanels: [], // Track which farm panels are expanded
        cropHeaders: [
            { text: 'Crop Type', value: 'cropType' },
            { text: 'Area', value: 'area' },
            { text: 'Planting Date', value: 'plantingDate' },
            { text: 'Expected Harvest', value: 'expectedHarvest' },
        ],
        projectDetailsExpanded: [0], // Always expanded
        userName: '',
      };
    },
    computed: {
      filteredSdgs() {
        return this.sdgs.filter(sdg => this.selectedSdgs.includes(sdg.id));
      },
      breadcrumbs() {
        return [
          {
            text: this.$t("carbonCredit.dashboard"),
            disabled: false,
            href: "/dashboard",
            exact: true,
          },
          {
            text: this.$t("carbonCredit.title"),
            disabled: false,
            href: "/carbon-credit/projects",
          },
          {
            text: this.$t("carbonCredit.user.title"),
            disabled: false,
            href: "/carbon-credit/users",
          },
          {
            text: this.userName || 'User Details',
            disabled: true,
            href: "breadcrumbs_link_3",
            exact: true,
          },
        ];
      },
      projectFarms() {
        return this.userFarms.filter(farm => farm.projectFarmId);
      },
      allFarmsAccepted() {
        return this.projectFarms.length > 0 && this.projectFarms.every(farm => farm.status === 'Accepted');
      },
      allFarmsRejected() {
        return this.projectFarms.length > 0 && this.projectFarms.every(farm => farm.status === 'Rejected');
      },
    },
    watch: {
      projectDetailsExpanded: {
        handler() {
          // Only enforce expansion on initial load
          if (this.isInitialLoad) {
            this.$nextTick(() => {
              this.projectDetailsExpanded = [0];
            });
            this.isInitialLoad = false; // Set to false after initial load
          }
        },
        immediate: true
      },
      expandedFarmPanels: {
        handler(newPanels, oldPanels) {
          // When panels are expanded, verify geofence for those farms on-demand
          if (Array.isArray(newPanels) && newPanels.length > 0) {
            // Find newly expanded panels (panels that are in newPanels but not in oldPanels)
            const oldPanelsSet = new Set(oldPanels || []);
            const newlyExpanded = newPanels.filter(index => !oldPanelsSet.has(index));
            
            if (newlyExpanded.length > 0) {
              // Verify geofence for newly expanded panels that haven't been verified yet
              const farmsToVerify = newlyExpanded
                .map(index => this.projectFarms[index])
                .filter(farm => {
                  if (!farm || !farm.id) return false;
                  // Skip if already verified or currently loading
                  if (this.verifiedFarms.has(farm.id) || this.loadingFarms.has(farm.id)) {
                    return false;
                  }
                  // Only verify farms that have lat/log (need geofence verification)
                  return farm.hasLatLog;
                });
              
              if (farmsToVerify.length > 0) {
                // Execute all verifications in parallel
                const verificationPromises = farmsToVerify.map(farm => this.verifyGeofenceAvailability(farm.id));
                Promise.all(verificationPromises).catch(() => {
                  // Silently handle verification errors
                });
              }
            }
          }
        },
        immediate: false
      }
    },
    async mounted() {
      // Ensure project details is always expanded on initial load
      this.projectDetailsExpanded = [0];
      await this.loadUserData();
      
      // Set initial load to false after ensuring expansion
      this.$nextTick(() => {
        this.isInitialLoad = false;
      });
    },
    methods: {
      decodeHtmlEntities(text) {
        if (!text || typeof text !== 'string') {
          return text;
        }
        // Create a temporary element to decode HTML entities
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
      },
      async loadUserData() {
        const userId = this.$route.params.id;
        const projectId = this.$route.query.project;
        
        if (!userId) {
          return;
        }

        try {
          this.startLoading();
          const res = await CarbonCreditService.getCarbonCreditUserById(userId, projectId);
          if (res.success && res.data) {
            const { user, project } = res.data;
            
            // Set project data if available
            if (project) {
              this.projectId = project.id;
              const d = project;
              this.form.project_title = d.project_title || 'N/A';
              this.form.project_type = d.project_type || 'N/A';
              this.form.description = d.description || 'N/A';
              this.form.status = d.status || 'N/A';
              this.form.creditType = d.credit_type || 'N/A';
              this.form.creditStart = d.credit_start_date?.split('T')[0] || 'N/A';
              this.form.creditEnd = d.credit_end_date?.split('T')[0] || 'N/A';
              this.form.standard = d.standard_methodology || 'N/A';
              this.form.country = d.country || 'N/A';
              this.form.dateJoined = d.date_joined && d.date_joined !== 'N/A' ? (typeof d.date_joined === 'string' ? d.date_joined.split('T')[0] : d.date_joined) : 'N/A';
              this.form.cropGrowingPractice = d.crop_growing_practice || 'N/A';
            }
            
            // Set user name
            if (user) {
              const firstName = user.firstName || '';
              const lastName = user.lastName || '';
              this.userName = `${firstName} ${lastName}`.trim() || 'User';
            }
            
            // Map user farms data
            if (user && user.farms && Array.isArray(user.farms)) {
              const farmsData = user.farms
                .filter(farm => !projectId || (farm.projectFarms && farm.projectFarms.length > 0))
                .map(farm => {
                  const projectFarm = farm.projectFarms && farm.projectFarms.length > 0 ? farm.projectFarms[0] : null;
                  const statusMap = {
                    'approved': 'Accepted',
                    'in_review': 'Pending',
                    'rejected': 'Rejected',
                    'require_update': 'Require Update',
                    'in_progress': 'Pending',
                    'approved_incomplete': 'Accepted',
                  };
                  const rawStatus = projectFarm?.status;
                  const normalizedRaw = typeof rawStatus === 'string' ? rawStatus.toLowerCase().replace(/-/g, '_') : '';
                  const displayStatus = projectFarm ? (statusMap[normalizedRaw] || statusMap[rawStatus] || rawStatus || 'Pending') : 'Pending';

                  return {
                    id: farm.id,
                    projectFarmId: projectFarm?.id || null, // Store projectFarm ID for status updates
                    name: this.decodeHtmlEntities(farm.farmName || 'N/A'),
                    status: displayStatus,
                    rawStatus: rawStatus || 'in_review', // Store raw status for API calls
                    dateOfRegistration: farm.createdAt ? new Date(farm.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : 'N/A',
                    farmRegistrationId: farm.farmRegistrationId || farm.recordId || 'N/A',
                    dimitraFarmId: farm.recordId ? `FR-${farm.recordId}` : 'N/A',
                    notTheFarmer: 'N/A', // Field doesn't exist in DB
                    farmerFirstName: farm.farmerFirstName || user.firstName || 'N/A',
                    farmerMiddleName: farm.farmerMiddleName || 'N/A',
                    farmerLastName: farm.farmerLastName || user.lastName || 'N/A',
                    farmerRegistrationId: farm.farmerRegistrationId || 'N/A',
                    dimitraFarmerId: farm.dimitraFarmId || user.id?.toString() || 'N/A',
                    farmGeofence: 'Unmapped', // Default to Unmapped, will be verified
                    geofenceAvailable: false, // Will be verified below
                    totalFarmSize: farm.area ? `${farm.area} Acre` : 'N/A',
                    mainGoals: 'N/A', // Field doesn't exist in DB - would need to join with farmingGoalOptId
                    ownershipType: farm.farmOwnershipType || 'N/A',
                    certifications: 'N/A', // Field doesn't exist in DB - would need to join with farm_certifications
                    productionType: farm.productionType || 'N/A',
                    landTitle: farm.farmTitleDocument || (projectFarm?.land_title_permit_s3_url ? 'Land Title-Document.PDF' : 'N/A'),
                    farmLocationAddress: farm.address || 'N/A',
                    farmCoordinate: (farm.lat && farm.log) ? `(${farm.lat}/${farm.log})` : 'N/A',
                    signedAgreementUrl: projectFarm?.signed_agreement_s3_url || null,
                    landTitleUrl: projectFarm?.land_title_permit_s3_url || null,
                    hasLatLog: !!(farm.lat && farm.log), // Store if lat/log exists for verification
                  };
                });
              
              // Preserve verified farms for farms that still exist
              const previousVerifiedFarms = new Set(this.verifiedFarms);
              const newFarmIds = new Set(farmsData.map(f => f.id));
              
              // Set all farms data
              this.userFarms = farmsData;
              
              // Preserve verified farms that still exist, clear others
              this.verifiedFarms = new Set(
                Array.from(previousVerifiedFarms).filter(farmId => newFarmIds.has(farmId))
              );
              
              // Clear loading state and expanded panels
              this.loadingFarms = new Set();
              this.expandedFarmPanels = [];
              
              // Initially verify only first 5 farms that haven't been verified yet
              await this.verifyGeofenceAvailabilityBatch(0, 5);
            } else {
              this.userFarms = [];
              this.verifiedFarms = new Set();
              this.loadingFarms = new Set();
              this.expandedFarmPanels = [];
            }
          }
        } catch (err) {
          this.$toast?.error('Failed to load user details');
        } finally {
          this.stopLoading();
        }
      },
      async verifyGeofenceAvailabilityBatch(startIndex = 0, endIndex = null) {
        // Get the range of farms to verify (defaults to all if endIndex not provided)
        const farmsToVerify = endIndex !== null 
          ? this.userFarms.slice(startIndex, endIndex).filter(farm => farm.hasLatLog && !this.verifiedFarms.has(farm.id))
          : this.userFarms.filter(farm => farm.hasLatLog && !this.verifiedFarms.has(farm.id));
        
        if (farmsToVerify.length === 0) {
          return;
        }

        // Verify geofence availability for each farm in parallel
        const verificationPromises = farmsToVerify.map(async (farm) => {
          try {
            this.loadingFarms.add(farm.id);
            this.$forceUpdate();
            
            const farmData = await FarmService.getFarm(farm.id);
            if (farmData && (farmData.segments?.length > 0 || farmData.GeoFences?.length > 0)) {
              // Geofence exists
              farm.farmGeofence = 'Mapped';
              farm.geofenceAvailable = true;
            } else {
              // No geofence data found
              farm.farmGeofence = 'Unmapped';
              farm.geofenceAvailable = false;
            }
            this.verifiedFarms.add(farm.id);
          } catch (error) {
            // 404 or other error - geofence doesn't exist
            if (error?.response?.status === 404 || error?.code === 404) {
              farm.farmGeofence = 'Unmapped';
              farm.geofenceAvailable = false;
            }
            // Silently handle other errors - keep as Unmapped
            this.verifiedFarms.add(farm.id); // Mark as verified even on error to avoid retrying
          } finally {
            this.loadingFarms.delete(farm.id);
            this.$forceUpdate();
          }
        });

        await Promise.all(verificationPromises);
      },
      async verifyGeofenceAvailability(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        
        if (!farm || !farm.hasLatLog) {
          return;
        }

        // Check if already verified or currently loading
        if (this.verifiedFarms.has(farmId) || this.loadingFarms.has(farmId)) {
          return;
        }

        try {
          this.loadingFarms.add(farmId);
          // Force update to show loading state
          this.$forceUpdate();
          
          const farmData = await FarmService.getFarm(farm.id);
          if (farmData && (farmData.segments?.length > 0 || farmData.GeoFences?.length > 0)) {
            // Geofence exists
            farm.farmGeofence = 'Mapped';
            farm.geofenceAvailable = true;
          } else {
            // No geofence data found
            farm.farmGeofence = 'Unmapped';
            farm.geofenceAvailable = false;
          }
          
          this.verifiedFarms.add(farmId);
        } catch (error) {
          // 404 or other error - geofence doesn't exist
          if (error?.response?.status === 404 || error?.code === 404) {
            farm.farmGeofence = 'Unmapped';
            farm.geofenceAvailable = false;
          }
          // Silently handle other errors - keep as Unmapped
          this.verifiedFarms.add(farmId); // Mark as verified even on error to avoid retrying
        } finally {
          // Ensure loading state is cleared
          this.loadingFarms.delete(farmId);
          // Force update to ensure UI reflects the change
          this.$forceUpdate();
        }
      },
      filterOrganizations() {
        this.orgPage = 1;
        this.updatePagination();
      },
      changeOrgPage(next) {
        this.orgPage += next ? 1 : -1;
        this.updatePagination();
      },
      getFileIcon(fileType) {
        if (fileType.startsWith('video')) return 'mdi-play-circle';
        if (fileType.startsWith('audio')) return 'mdi-music';
        return 'mdi-file-document';
      },
    
      updatePagination() {
        const filtered = this.orgSearch
          ? this.organizations.filter(org =>
              org.name?.toLowerCase().includes(this.orgSearch.toLowerCase()) ||
              org.address?.toLowerCase().includes(this.orgSearch.toLowerCase()) ||
              org.country?.toLowerCase().includes(this.orgSearch.toLowerCase()) ||
              org.website?.toLowerCase().includes(this.orgSearch.toLowerCase())
            )
          : [...this.organizations];
  
        this.orgTotalPages = Math.ceil(filtered.length / this.limit);
        const start = (this.orgPage - 1) * this.limit;
        const end = this.orgPage * this.limit;
  
        this.filteredOrganizations = filtered.slice(start, end);
        this.orgFrom = filtered.length ? start + 1 : 0;
        this.orgTo = Math.min(end, filtered.length);
      },
      previewProject() {
        this.$store.dispatch('project/setCurrentProject', {
          ...this.form,
          office: this.office,
          organizations: this.organizations,
          sdgs: this.sdgs,
          selectedSdgs: this.selectedSdgs,
          dataCollectionModules: this.dataCollectionModules,
          vintageRows: this.form.vintageRows,
          projectId: this.projectId
        });
        this.$router.push({
          path: `/carbon-credit/projects/preview/${this.projectId}`
        });
      },
      rejectAll() {
        const projectFarms = this.userFarms.filter(farm => farm.projectFarmId);
        const allAlreadyRejected = projectFarms.length > 0 && projectFarms.every(farm => farm.status === 'Rejected');
        if (allAlreadyRejected) {
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.allFarmsAlreadyRejected') || 'All farms are already rejected.',
            type: 'info'
          });
          return;
        }
        if (projectFarms.length === 0) {
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.noFarmsToReject') || 'No farms to reject',
            type: 'info'
          });
          return;
        }
        this.showConfirmDialog = true;
        this.confirmActionType = 'rejectAll';
        this.confirmTitle = this.$t('carbonCredit.user.confirmRejectAllTitle');
        this.confirmMessage = this.$t('carbonCredit.user.confirmRejectAll');
        this.confirmButtonText = this.$t('carbonCredit.user.confirmRejectAllButton');
      },
      acceptAll() {
        const projectFarms = this.userFarms.filter(farm => farm.projectFarmId);
        const allAlreadyAccepted = projectFarms.length > 0 && projectFarms.every(farm => farm.status === 'Accepted');
        if (allAlreadyAccepted) {
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.allFarmsAlreadyAccepted') || 'All farms are already accepted.',
            type: 'info'
          });
          return;
        }
        if (projectFarms.length === 0) {
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.noFarmsToAccept') || 'No farms to accept',
            type: 'info'
          });
          return;
        }
        this.showConfirmDialog = true;
        this.confirmActionType = 'acceptAll';
        this.confirmTitle = this.$t('carbonCredit.user.confirmAcceptAllTitle');
        this.confirmMessage = this.$t('carbonCredit.user.confirmAcceptAll');
        this.confirmButtonText = this.$t('carbonCredit.user.confirmAcceptAllButton');
      },
      async handleConfirmAction() {
        this.showConfirmDialog = false;
        if (this.confirmActionType === 'rejectAll') {
          await this.rejectAllFarms();
        } else if (this.confirmActionType === 'acceptAll') {
          await this.acceptAllFarms();
        } else if (this.confirmActionType === 'acceptFarm') {
          await this.acceptFarmAction(this.pendingFarmId);
        } else if (this.confirmActionType === 'rejectFarm') {
          await this.rejectFarmAction(this.pendingFarmId);
        }
        this.pendingFarmId = null;
      },
      async rejectAllFarms() {
        try {
          this.startLoading();
          const projectFarms = this.userFarms.filter(farm => farm.projectFarmId);
          const farmsToReject = projectFarms.filter(farm => farm.status !== 'Rejected');
          
          if (farmsToReject.length === 0) {
            this.$notify({
              title: this.$t('carbonCredit.success'),
              text: projectFarms.length > 0
                ? (this.$t('carbonCredit.user.allFarmsAlreadyRejected') || 'All farms are already rejected.')
                : (this.$t('carbonCredit.user.noFarmsToReject') || 'No farms to reject'),
              type: 'info'
            });
            this.stopLoading();
            return;
          }

          const promises = farmsToReject.map(farm =>
            CarbonCreditService.updateProjectFarmStatus(farm.projectFarmId, 'rejected')
          );

          await Promise.all(promises);
          
          // Refresh user data
          await this.loadUserData();
          
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.allFarmsRejected') || 'All farms rejected successfully',
            type: 'success'
          });
        } catch (error) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: error?.response?.data?.message || this.$t('carbonCredit.user.rejectAllError') || 'Failed to reject all farms',
            type: 'error'
          });
        } finally {
          this.stopLoading();
        }
      },
      async acceptAllFarms() {
        try {
          this.startLoading();
          const projectFarms = this.userFarms.filter(farm => farm.projectFarmId);
          const farmsToAccept = projectFarms.filter(farm => farm.status !== 'Accepted');
          
          if (farmsToAccept.length === 0) {
            this.$notify({
              title: this.$t('carbonCredit.success'),
              text: projectFarms.length > 0
                ? (this.$t('carbonCredit.user.allFarmsAlreadyAccepted') || 'All farms are already accepted.')
                : (this.$t('carbonCredit.user.noFarmsToAccept') || 'No farms to accept'),
              type: 'info'
            });
            this.stopLoading();
            return;
          }

          const promises = farmsToAccept.map(farm =>
            CarbonCreditService.updateProjectFarmStatus(farm.projectFarmId, 'approved_incomplete')
          );

          await Promise.all(promises);
          
          // Refresh user data
          await this.loadUserData();
          
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.allFarmsAccepted') || 'All farms accepted successfully',
            type: 'success'
          });
        } catch (error) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: error?.response?.data?.message || this.$t('carbonCredit.user.acceptAllError') || 'Failed to accept all farms',
            type: 'error'
          });
        } finally {
          this.stopLoading();
        }
      },
      rejectFarm(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm || !farm.projectFarmId) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: this.$t('carbonCredit.user.farmNotFound') || 'Farm not found or not associated with project',
            type: 'error'
          });
          return;
        }

        if (farm.status === 'Rejected') {
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.farmAlreadyRejected') || 'Farm is already rejected',
            type: 'info'
          });
          return;
        }

        this.pendingFarmId = farmId;
        this.showConfirmDialog = true;
        this.confirmActionType = 'rejectFarm';
        this.confirmTitle = this.$t('carbonCredit.user.confirmRejectFarmTitle');
        this.confirmMessage = this.$t('carbonCredit.user.confirmRejectFarm');
        this.confirmButtonText = this.$t('carbonCredit.user.confirmRejectFarmButton');
      },
      async rejectFarmAction(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm || !farm.projectFarmId) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: this.$t('carbonCredit.user.farmNotFound') || 'Farm not found or not associated with project',
            type: 'error'
          });
          return;
        }

        try {
          this.startLoading();
          await CarbonCreditService.updateProjectFarmStatus(farm.projectFarmId, 'rejected');
          
          // Refresh user data
          await this.loadUserData();
          
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.farmRejected') || 'Farm rejected successfully',
            type: 'success'
          });
        } catch (error) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: error?.response?.data?.message || this.$t('carbonCredit.user.rejectFarmError') || 'Failed to reject farm',
            type: 'error'
          });
        } finally {
          this.stopLoading();
        }
      },
      acceptFarm(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm || !farm.projectFarmId) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: this.$t('carbonCredit.user.farmNotFound') || 'Farm not found or not associated with project',
            type: 'error'
          });
          return;
        }

        if (farm.status === 'Accepted') {
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.farmAlreadyAccepted') || 'Farm is already accepted',
            type: 'info'
          });
          return;
        }

        this.pendingFarmId = farmId;
        this.showConfirmDialog = true;
        this.confirmActionType = 'acceptFarm';
        this.confirmTitle = this.$t('carbonCredit.user.confirmAcceptFarmTitle');
        this.confirmMessage = this.$t('carbonCredit.user.confirmAcceptFarm');
        this.confirmButtonText = this.$t('carbonCredit.user.confirmAcceptFarmButton');
      },
      async acceptFarmAction(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm || !farm.projectFarmId) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: this.$t('carbonCredit.user.farmNotFound') || 'Farm not found or not associated with project',
            type: 'error'
          });
          return;
        }

        try {
          this.startLoading();
          await CarbonCreditService.updateProjectFarmStatus(farm.projectFarmId, 'approved_incomplete');
          
          // Refresh user data
          await this.loadUserData();
          
          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.farmAccepted') || 'Farm accepted successfully',
            type: 'success'
          });
        } catch (error) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: error?.response?.data?.message || this.$t('carbonCredit.user.acceptFarmError') || 'Failed to accept farm',
            type: 'error'
          });
        } finally {
          this.stopLoading();
        }
      },
      async viewGeofence(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm) {
          this.$toast?.error(this.$t('carbonCredit.user.farmNotFound') || 'Farm not found');
          return;
        }

        if (!farm.id) {
          this.$toast?.error('Farm ID is required');
          return;
        }

        if (!farm.geofenceAvailable) {
          return; // Button should be disabled, but handle gracefully
        }

        try {
          this.startLoading();
          // Fetch full farm details including geofence data
          const farmData = await FarmService.getFarm(farm.id);
          
          if (!farmData) {
            // Update farm status to Unmapped
            farm.farmGeofence = 'Unmapped';
            farm.geofenceAvailable = false;
            this.$toast?.error('Farm geofence data not found');
            this.stopLoading();
            return;
          }

          // Store farm data in the format expected by ViewGeofence component
          this.selectedFarmForGeofence = {
            farm: farmData,
            farmType: this.determineGeofenceType(farmData)
          };
          
          // Show dialog
          this.showGeofenceDialog = true;
        } catch (error) {
          // Handle 404 error specifically
          if (error?.response?.status === 404 || error?.code === 404) {
            // Update farm status to Unmapped and disable button
            farm.farmGeofence = 'Unmapped';
            farm.geofenceAvailable = false;
            this.$toast?.error(this.$t('carbonCredit.user.geofenceNotFound') || 'Geofence data not found');
          } else {
            this.$toast?.error('Failed to load farm geofence data');
          }
        } finally {
          this.stopLoading();
        }
      },
      determineGeofenceType(farm) {
        // Check for segments data first
        if (farm.segments && farm.segments.length > 0) {
          const segment = farm.segments[0];
          
          // Check if it's a circular geofence (POINT)
          if (segment.geofenceRadius && segment.geofenceCenterLat && segment.geofenceCenterLog) {
            return 'POINT';
          }
          
          // Check if it's a polygon geofence
          if (segment.coordinates && segment.coordinates.length > 0) {
            return 'POLYGON';
          }
        }
        
        // Check for GeoFences (circular geofence)
        if (farm.GeoFences && farm.GeoFences.length > 0) {
          const geoFence = farm.GeoFences[0];
          if (geoFence.geofenceCenterLat && geoFence.geofenceCenterLog && geoFence.geofenceRadius) {
            return 'POINT';
          }
        }
        
        // Default to POLYGON
        return 'POLYGON';
      },
      closeGeofenceDialog() {
        this.showGeofenceDialog = false;
        this.selectedFarmForGeofence = null;
      },
      viewLandTitle(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (farm && farm.landTitleUrl) {
          window.open(farm.landTitleUrl, '_blank');
        } else {
          this.$toast.info('Land title document not available');
        }
      },
      viewAgreement(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm) {
          this.$toast?.error(this.$t('carbonCredit.user.farmNotFound') || 'Farm not found');
          return;
        }
        
        if (!farm.signedAgreementUrl) {
          return;
        }
        
        try {
          // Open the agreement URL in a new tab
          window.open(farm.signedAgreementUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
          this.$toast?.error(this.$t('carbonCredit.user.agreementOpenError') || 'Failed to open agreement document');
        }
      },
      viewFarmData(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm || !farm.projectFarmId) {
          this.$toast?.error('Farm not found or not associated with project');
          return;
        }
        this.$router.push({
          name: 'FarmSubmission',
          params: { 
            farmId: farmId,
            projectFarmId: farm.projectFarmId,
            farmName: farm?.name || 'Farm'
          }
        });
      },
      triggerAgreementFileInput() {
        if (this.$refs.agreementFileInput) {
          this.$refs.agreementFileInput.click();
        }
      },
      handleAgreementDrop(event) {
        event.preventDefault();
        this.agreementDragging = false;
        const files = event.dataTransfer?.files;
        if (!files || !files.length) {
          return;
        }
        this.setAgreementFile(files[0]);
      },
      onAgreementFileChange(event) {
        const files = event?.target?.files || [];
        if (!files.length) {
          return;
        }
        this.setAgreementFile(files[0]);
      },
      setAgreementFile(file) {
        this.agreementFile = file;
        this.agreementDocuments = [file];
      },
      removeAgreementFile() {
        this.agreementFile = null;
        this.agreementDocuments = [];
        if (this.$refs.agreementFileInput) {
          this.$refs.agreementFileInput.value = null;
        }
      },
      getAgreementFileExtension(doc) {
        const name = doc?.name || doc?.file_name || '';
        const parts = name.split('.');
        if (parts.length > 1) {
          return parts[parts.length - 1].toUpperCase();
        }
        return 'FILE';
      },
      getAgreementFileName(doc) {
        return doc?.name || doc?.file_name || this.$t('carbonCredit.user.farmerAgreement') || 'Document';
      },
      openAgreementUploadDialog(farmId) {
        const farm = this.userFarms.find(f => f.id === farmId);
        if (!farm || !farm.projectFarmId) {
          this.$toast?.error(this.$t('carbonCredit.user.farmNotFound') || 'Farm not found or not associated with project');
          return;
        }

        this.agreementUploadFarm = farm;
        this.agreementFile = null;
        this.showAgreementUploadDialog = true;
      },
      closeAgreementUploadDialog() {
        this.showAgreementUploadDialog = false;
        this.agreementUploadFarm = null;
        this.agreementFile = null;
        this.uploadingAgreement = false;
      },
      async submitAgreementUpload() {
        if (!this.agreementUploadFarm || !this.agreementUploadFarm.projectFarmId) {
          this.$toast?.error(this.$t('carbonCredit.user.farmNotFound') || 'Farm not found or not associated with project');
          return;
        }

        const file = Array.isArray(this.agreementFile) ? this.agreementFile[0] : this.agreementFile;
        if (!file) {
          return;
        }

        try {
          this.uploadingAgreement = true;
          await CarbonCreditService.uploadSignedAgreement(this.agreementUploadFarm.projectFarmId, file);

          await this.loadUserData();

          this.$notify({
            title: this.$t('carbonCredit.success'),
            text: this.$t('carbonCredit.user.agreementUploadSuccess') || 'Signed agreement uploaded successfully',
            type: 'success'
          });

          this.closeAgreementUploadDialog();
        } catch (error) {
          this.$notify({
            title: this.$t('carbonCredit.error'),
            text: error?.response?.data?.message || this.$t('carbonCredit.user.agreementUploadError') || 'Failed to upload signed agreement',
            type: 'error'
          });
        } finally {
          this.uploadingAgreement = false;
        }
      },
      getFarmDisplayStatus(farm) {
        const raw = (farm?.rawStatus || '').toLowerCase().replace(/-/g, '_');
        const statusMap = {
          in_progress: { label: this.$t('carbonCredit.user.statusInProgress'), warning: false },
          in_review: { label: this.$t('carbonCredit.user.statusInReview'), warning: false },
          approved_incomplete: { label: this.$t('carbonCredit.user.statusApprovedWithWarning'), warning: true },
          approved: { label: this.$t('carbonCredit.user.statusApproved'), warning: false },
          require_update: { label: this.$t('carbonCredit.user.statusRequireUpdate'), warning: true },
          rejected: { label: this.$t('carbonCredit.user.statusRejected'), warning: false },
        };
        return statusMap[raw] || { label: this.$t('carbonCredit.user.statusInReview'), warning: false };
      }
    },
  };
  </script>
  
  
  <style scoped>
  .view-block {
    background-color: #E5F8F1;
    padding: 1rem;
    border-radius: 6px;
    margin-bottom: 1rem;
  }
  .view-label {
    font-size: 16px;
    color: #555;
    margin-bottom: 4px;
    display: block;
    font-weight: 300;
  }
  .view-value {
    font-weight: bold;
    font-size: 1rem;
    color: #333;
  }
  .multi-line {
    white-space: pre-wrap;
  }
  .bordered-expansion {
    border: 1px solid #f0c96c;
    border-radius: 6px;
    background-color: #fff5e1;
  }
  .v-data-table {
    /* Remove background and border for organization details table */
    background-color: transparent;
    border-radius: 0;
    border: none;
  }

  .v-data-table /deep/ th {
    background-color: #00BD73;
    color: white !important;
  }
  .pdf-box {
    height: 90px;
    width: 90px;
    background-color: #e5f8f1;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    border-radius: 4px;
  }
  .file-extension {
    font-size: 16px;
    color: #135b00;
    margin: 0;
    line-height: 1;
    font-weight: 600;
  }
  .file-name {
    display: block;
    font-size: 16px;
    color: #333;
    margin-top: 5px;
    text-align: center;
    word-break: break-all;
    font-weight: 600;
  }
  .docs-box {
    display: flex;
    justify-content: center;
  }
  .docs-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .img-item {
    display: flex;
    flex-direction: column;
    align-items: left;
  }
  .dimitra-office-block {
    background-color: #E5F8F1;
    border-radius: 8px;
    padding: 24px 20px 16px 20px;
    margin-bottom: 18px;
    min-height: 120px;
    min-width: 220px;
    box-sizing: border-box;
  }
  .dimitra-office-label {
    font-size: 16px;
    color: #757575;
    font-weight: 400;
    margin-bottom: 2px;
    margin-right: 4px;
  }
  .dimitra-office-value {
    font-size: 16px;
    color: #222;
    font-weight: 600;
    margin-bottom: 0;
    margin-top: 4px;
    word-break: break-word;
  }
  .normal-text {
    font-weight: normal !important;
  }
  .bold-title {
    font-weight: bold !important;
  }
  .description-height {
    min-height: 40px; /* Adjust as needed for consistent height */
  }
  .button-container {
    display: flex;
    justify-content: flex-start; /* Align buttons to the start */
  }
  .upload-document {
    width: 100%;
  }
  .dropZone {
    width: 100%;
    max-width: 720px;
    height: 200px;
    position: relative;
    border: 2px dashed #97a8b8;
  }
  .dropZone:hover {
    border: 2px solid #00BD73;
  }
  .dropZone-info {
    color: #a8a8a8;
    position: absolute;
    top: 50%;
    width: 100%;
    transform: translate(0, -50%);
    text-align: center;
    font-size: 12px;
  }
  .dropZone-info .underline {
    text-decoration: underline;
    color: #0057bd;
  }
  .dropZone-info .blue-color {
    color: #0057bd;
  }
  .dropZone input {
    position: absolute;
    cursor: pointer;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
  }
  .dropZone-over {
    background: #5c5c5c;
    opacity: 0.8;
  }
  .pdf-docs {
    display: flex;
    justify-content: flex-start;
  }
  </style>
  <style lang="scss">
  /* Tooltip outline – not scoped so it applies to portaled tooltip content */
  .filter-summary-tooltip {
    border: 1px solid #9e9e9e !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  </style>